import { Save } from "lucide-react";
import { useState } from "react";

export function SettingsHeader({ title, description, onSave, saving, saved, testId }) {
  return (
    <>
      <div className="flex justify-between items-center mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">{title}</h1>
          {description && <p className="text-sm text-gray-600">{description}</p>}
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid={testId || "settings-save"}
          className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25] disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
      {saved && <p className="text-green-700 text-sm mb-4">✓ Cambios guardados.</p>}
    </>
  );
}

export function Section({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 p-6">
      <h2 className="font-semibold text-[#0F2040] mb-4">{title}</h2>
      {children}
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
      />
    </div>
  );
}

export function Textarea({ label, value, onChange, rows = 4, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
      />
    </div>
  );
}

export function Loading() {
  return <p className="text-gray-400">Cargando…</p>;
}

// Renders 4 tabbed inputs (ES · FR · EN · AR) for a single settings field.
// Pass `prefix` (e.g. "home_welcome_kicker") and it will read/write
// `${prefix}_es`, `${prefix}_fr`, `${prefix}_en`, `${prefix}_ar` on `settings`.
// Arabic tab uses dir="rtl".
const LANG_TABS = [
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

export function MultiLangField({ label, prefix, settings, set, textarea = false, rows = 4 }) {
  const [tab, setTab] = useState("es");
  const key = `${prefix}_${tab}`;
  const value = settings?.[key] ?? "";
  const isAr = tab === "ar";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      <div className="border-b border-gray-200 flex items-center gap-1 mb-1.5" data-testid={`multilang-tabs-${prefix}`}>
        {LANG_TABS.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setTab(l.code)}
            data-testid={`multilang-tab-${prefix}-${l.code}`}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
              tab === l.code
                ? "border-[#C8102E] text-[#0F2040]"
                : "border-transparent text-gray-500 hover:text-[#0F2040]"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
      {textarea ? (
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => set(key, e.target.value)}
          dir={isAr ? "rtl" : "ltr"}
          data-testid={`multilang-input-${prefix}-${tab}`}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => set(key, e.target.value)}
          dir={isAr ? "rtl" : "ltr"}
          data-testid={`multilang-input-${prefix}-${tab}`}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
        />
      )}
    </div>
  );
}
